import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HelpDrawer } from '../components/HelpDrawer';
import { CompassIcon } from '../components/icons';
import { useTours } from '../tours/useTours';
import { findTour } from '../tours/tours';
import { HelpContext, type HelpContextValue } from './helpContext';
import { useHelpArticles } from './useHelpArticles';
import { HelpArticleBody } from './HelpArticleBody';

/** Props for {@link HelpProvider}. */
export interface HelpProviderProps {
  /** Application subtree that can trigger contextual help. */
  children: ReactNode;
}

/**
 * Provides contextual-help controls and renders the single global HelpDrawer.
 *
 * Any component below can call `useHelp().openArticle(slug)` (e.g. a HelpTip's
 * "Learn more") to slide the article in without leaving the current screen
 * (spec 09 §2). The drawer also links through to the full Help Centre.
 */
export function HelpProvider({ children }: HelpProviderProps): JSX.Element {
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const { startTour, tours } = useTours();

  const openArticle = useCallback((slug: string) => setOpenSlug(slug), []);
  const close = useCallback(() => setOpenSlug(null), []);

  const value = useMemo<HelpContextValue>(
    () => ({ openSlug, openArticle, close }),
    [openSlug, openArticle, close],
  );

  const help = useHelpArticles();
  const article = openSlug !== null && help !== null ? help.findArticle(openSlug) : undefined;
  // Only offer the tour button when the referenced tour actually exists.
  const relatedTour = article?.tourId !== undefined ? findTour(article.tourId, tours) : undefined;
  // Resolve cross-links, dropping any that no longer point at a real article.
  const relatedArticles = article?.related !== undefined ? (help?.findArticles(article.related) ?? []) : [];

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpDrawer
        open={openSlug !== null}
        onClose={close}
        title={article?.title ?? 'Help'}
        subtitle={article?.category}
        footer={
          <Link
            to="/help"
            onClick={close}
            className="inline-flex items-center text-sm font-medium text-brand-600 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-300"
          >
            Open the Help Centre
          </Link>
        }
      >
        {article ? (
          <div className="space-y-4 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            <div className="space-y-3">
              <p className="font-medium text-slate-900 dark:text-slate-100">{article.summary}</p>
              <HelpArticleBody body={article.body} />
            </div>

            {relatedTour !== undefined ? (
              <button
                type="button"
                onClick={() => {
                  close();
                  startTour(relatedTour.id);
                }}
                className="inline-flex items-center gap-2 rounded-md bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:bg-brand-500/10 dark:text-brand-200 dark:hover:bg-brand-500/20"
              >
                <CompassIcon size={16} />
                Take the tour: {relatedTour.title}
              </button>
            ) : null}

            {article.links !== undefined && article.links.length > 0 ? (
              <nav aria-label="Related screens" className="border-t border-slate-200 pt-3 dark:border-navy-border">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Go to
                </p>
                <ul className="space-y-1">
                  {article.links.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        onClick={close}
                        className="inline-flex items-center text-sm font-medium text-brand-600 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-300"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}

            {relatedArticles.length > 0 ? (
              <nav
                aria-label="Related articles"
                className="border-t border-slate-200 pt-3 dark:border-navy-border"
              >
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  Related articles
                </p>
                <ul className="space-y-1">
                  {relatedArticles.map((related) => (
                    <li key={related.slug}>
                      <button
                        type="button"
                        onClick={() => openArticle(related.slug)}
                        className="inline-flex items-center text-left text-sm font-medium text-brand-600 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-brand-300"
                      >
                        {related.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}

            {article.updated !== undefined ? (
              <p className="border-t border-slate-200 pt-3 text-xs text-slate-400 dark:border-navy-border dark:text-slate-500">
                Updated {article.updated}
                {article.version !== undefined ? ` · v${article.version}` : ''}
              </p>
            ) : null}
          </div>
        ) : openSlug !== null ? (
          // The article-content chunk hasn't resolved yet (see useHelpArticles):
          // open immediately with this placeholder rather than staying closed —
          // a first-time click otherwise gets no reaction at all, then the drawer
          // pops open unprompted a moment later once the chunk lands.
          <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : null}
      </HelpDrawer>
    </HelpContext.Provider>
  );
}
